import { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Mic, StopCircle, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { aiGatewayService } from '../services/aiGateway';
import type { VoiceToSoapResponse } from '../services/api/medicalRecordsApi';

/**
 * Best-effort parse of a partial JSON buffer streaming from the LLM. Walks
 * the buffer tracking string state + brace/bracket stack, then closes any
 * unterminated string and remaining open containers in LIFO order. The result
 * is a fully-valid JSON we can JSON.parse to recover whichever sub-fields
 * have already streamed. Closed array items are recoverable; items still in
 * the middle of streaming are filled with empty strings or omitted.
 *
 * Returns null if nothing usable can be parsed.
 *
 * Used to drive the streaming "cards arriving as the model writes them" UX —
 * we re-run this on every token and React re-renders the cards as new items
 * appear in the parsed structure.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function tryParsePartialBuffer(buffer: string): any | null {
  if (!buffer) return null;
  let text = buffer.trim();
  // Strip code fences if the model wrapped its JSON.
  const fence = text.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (fence) text = fence[1].trim();

  const start = text.indexOf('{');
  if (start === -1) return null;
  const candidate = text.slice(start);

  // Strict parse first (rare on a still-streaming buffer, but cheap).
  try {
    return JSON.parse(candidate);
  } catch {
    // fall through to repair
  }

  const stack: string[] = [];
  let inString = false;
  let escape = false;
  let lastNonWs = '';
  for (let i = 0; i < candidate.length; i++) {
    const ch = candidate[i];
    if (inString) {
      if (escape) {
        escape = false;
      } else if (ch === '\\') {
        escape = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }
    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === '{' || ch === '[') {
      stack.push(ch);
    } else if (ch === '}' || ch === ']') {
      stack.pop();
    }
    if (!/\s/.test(ch)) lastNonWs = ch;
  }

  let repaired = candidate;
  if (inString) {
    if (escape) repaired = repaired.slice(0, -1);
    repaired += '"';
  }
  const trimmed = repaired.replace(/\s+$/, '');
  if (trimmed.endsWith(',')) repaired = trimmed.slice(0, -1);
  if (lastNonWs === ':') repaired += ' ""';
  while (stack.length > 0) {
    const opener = stack.pop();
    repaired += opener === '{' ? '}' : ']';
  }

  try {
    return JSON.parse(repaired);
  } catch {
    return null;
  }
}

export interface ValidationStageEvent {
  id: 'extract' | 'schema' | 'drugs' | 'grounding' | 'verify' | 'safety';
  status: 'pending' | 'active' | 'done' | 'failed';
  detail?: string;
}

interface VoiceToSoapRecorderWebSocketProps {
  patientId?: string;
  medicalRecordId?: string;
  onSoapGenerated?: (response: VoiceToSoapResponse) => void;
  onTranscriptionComplete?: (transcription: string) => void;
  onError?: (error: string) => void;
  onValidationStage?: (event: ValidationStageEvent) => void;
  className?: string;
  soapSection?: 'subjective' | 'objective' | 'assessment' | 'plan' | 'full';
  disabled?: boolean;
  transcriptionOnly?: boolean;
}

export interface VoiceToSoapRecorderHandle {
  regenerateFromTranscript: (transcript: string) => void;
  sendFollowUp: (question: string) => void;
  isProcessing: () => boolean;
  isRecording: () => boolean;
  startRecording: () => void;
  stopRecording: () => void;
}

export const VoiceToSoapRecorderWebSocket = forwardRef<VoiceToSoapRecorderHandle, VoiceToSoapRecorderWebSocketProps>(({
  patientId,
  medicalRecordId,
  onSoapGenerated,
  onTranscriptionComplete,
  onError,
  onValidationStage,
  className = '',
  soapSection = 'full',
  disabled = false,
  transcriptionOnly = false,
}, ref) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [silenceWarning, setSilenceWarning] = useState(false);
  const silenceFramesRef = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [, setIsHeidiReady] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [transcription, setTranscription] = useState<string>('');

  // Store current SOAP data for follow-up context
  const currentSoapDataRef = useRef<VoiceToSoapResponse | null>(null);

  // Buffer of streamed LLM tokens for incremental SOAP rendering
  const streamingBufferRef = useRef<string>('');
  // Last successfully parsed structured snapshot — held across tokens so we
  // don't re-render or re-animate cards unless an item count actually changed.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const lastStructuredRef = useRef<any>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const sessionIdRef = useRef<string>('');
  const audioChunksRef = useRef<Blob[]>([]);
  const sendIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunkIndexRef = useRef<number>(0);
  // Tracks a started-but-not-yet-ended scribe session. Used by both the
  // useEffect cleanup and the beforeunload listener to free backend
  // buffers (sessionConfigs / audioBuffers / transcriptionBuffers in
  // scribe.processor.ts) when the user navigates away or closes the tab
  // mid-record. The singleton aiGatewayService keeps the socket open
  // across route changes, so backend disconnect cleanup never fires
  // until the whole tab dies — without this, every abandoned consult
  // leaves 30s+ of audio + transcript pinned in gateway RAM.
  const sessionEndedRef = useRef<boolean>(true);
  // Set true on unmount so the rotating MediaRecorder's `onstop` (which
  // can fire AFTER React has torn the component down) doesn't push a
  // stale chunk through the gateway against a session that's already
  // over.
  const unmountedRef = useRef<boolean>(false);

  // Forward refs for functions defined later
  const startRecordingRef = useRef<(() => void) | null>(null);
  const stopRecordingRef = useRef<(() => void) | null>(null);

  // Stable refs for callbacks to avoid socket reconnect on every render
  const onSoapGeneratedRef = useRef(onSoapGenerated);
  const onTranscriptionCompleteRef = useRef(onTranscriptionComplete);
  const onErrorRef = useRef(onError);
  const onValidationStageRef = useRef(onValidationStage);
  onSoapGeneratedRef.current = onSoapGenerated;
  onTranscriptionCompleteRef.current = onTranscriptionComplete;
  onErrorRef.current = onError;
  onValidationStageRef.current = onValidationStage;
  // Latest patientId without retriggering the effect that sets up the
  // gateway listeners (cleanup/beforeunload need it for end-recording).
  const patientIdRef = useRef(patientId);
  patientIdRef.current = patientId;

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    regenerateFromTranscript: async (transcript: string) => {
      try {
        // Ensure connection — connect() is idempotent and deduped
        if (!aiGatewayService.isConnected()) {
          await aiGatewayService.connect();
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to connect to voice service';
        setError(msg);
        onErrorRef.current?.(msg);
        return;
      }

      setIsProcessing(true);
      setError(null);
      streamingBufferRef.current = '';
      lastStructuredRef.current = null;

      // Send the edited transcript to backend for SOAP regeneration
      aiGatewayService.scribeRegenerate({
        transcript,
        patientId: patientId || '',
        medicalRecordId: medicalRecordId || '',
      });
    },
    sendFollowUp: (question: string) => {
      if (!aiGatewayService.isConnected()) {
        setError('Not connected to voice service');
        return;
      }

      setIsProcessing(true);
      setError(null);

      aiGatewayService.scribeFollowUp({
        sessionId: sessionIdRef.current,
        question,
        // Spread: SoapNote (interface, no index signature) → anonymous
        // object type, which satisfies ScribeSoapNote's index signature.
        currentSoap: currentSoapDataRef.current?.soapNote
          ? { ...currentSoapDataRef.current.soapNote }
          : null,
        transcription: currentSoapDataRef.current?.transcription || '',
      });
    },
    startRecording: () => {
      if (!isRecording && !isProcessing && startRecordingRef.current) {
        startRecordingRef.current();
      }
    },
    stopRecording: () => {
      if (isRecording && stopRecordingRef.current) {
        stopRecordingRef.current();
      }
    },
    isRecording: () => isRecording,
    isProcessing: () => isProcessing,
  }), [patientId, medicalRecordId, isRecording, isProcessing]);

  useEffect(() => {
    // Connect via the singleton gateway
    aiGatewayService.connect();

    // Subscribe to connection state changes
    const unsubConnection = aiGatewayService.onConnectionChange((state) => {
      const connected = state === 'connected';
      setIsConnected(connected);
      if (!connected) {
        setIsHeidiReady(false);
      }
      if (connected) {
        setError(null);
      }
    });

    // Scribe event subscriptions (events are prefixed with 'scribe.')
    const unsubConnectionReady = aiGatewayService.on('scribe.connection-ready', (data: { message: string }) => {
      console.log('HEIDI.LLM connection ready:', data.message);
      setIsHeidiReady(true);
      setStatusMessage(data.message);
    });
    // The event fires once per socket connect — seed from the latched
    // state when the socket was already warm before this mount.
    if (aiGatewayService.isScribeReady()) setIsHeidiReady(true);

    const unsubSessionStarted = aiGatewayService.on('scribe.session-started', (data: unknown) => {
      console.log('Session started:', data);
      setStatusMessage('Recording session started');
    });

    const unsubChunkReceived = aiGatewayService.on('scribe.chunk-received', (data: { chunksReceived: number }) => {
      console.log('Chunk received:', data.chunksReceived);
    });

    const unsubProcessing = aiGatewayService.on('scribe.processing', (data: { message: string }) => {
      console.log('Processing:', data);
      setStatusMessage(data.message);
      setIsProcessing(true);
    });

    const unsubProcessingUpdate = aiGatewayService.on('scribe.processing-update', (data: { content?: string }) => {
      const token = data?.content;
      if (!token) return;

      streamingBufferRef.current += token;
      const buffer = streamingBufferRef.current
        .replace(/^```(?:json)?\s*/i, '')
        .replace(/\s*```$/i, '');

      // Tolerantly extract the four SOAP string fields from a partial JSON buffer.
      // Each capture stops at the next unescaped " OR end-of-buffer if not yet streamed.
      const extract = (field: string): string => {
        const re = new RegExp(`"${field}"\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)`);
        const m = buffer.match(re);
        if (!m) return '';
        // Unescape common JSON escapes for display
        return m[1]
          .replace(/\\n/g, '\n')
          .replace(/\\"/g, '"')
          .replace(/\\\\/g, '\\');
      };

      const partialSoap = {
        subjective: extract('subjective'),
        objective: extract('objective'),
        assessment: extract('assessment'),
        plan: extract('plan'),
      };

      // Only re-parse the structured fields when a closing bracket arrives in
      // this token — that's the only time a sub-structure could have just
      // completed. Per-character re-parsing would otherwise produce flicker
      // as the lenient repair closes the buffer at slightly different points.
      if (token.includes('}') || token.includes(']')) {
        const newStructured = tryParsePartialBuffer(buffer);
        if (newStructured) {
          // Diff against the previous structured snapshot — only re-emit when
          // something meaningful changed, so React doesn't re-render the cards
          // on every parse and the entrance animation only fires for genuinely
          // new items.
          const prev = lastStructuredRef.current;
          const changed =
            !prev ||
            (newStructured.medications?.length || 0) !== (prev.medications?.length || 0) ||
            JSON.stringify(newStructured.vitals) !== JSON.stringify(prev.vitals) ||
            (newStructured.suggestedActions?.labOrders?.length || 0) !== (prev.suggestedActions?.labOrders?.length || 0) ||
            (newStructured.suggestedActions?.appointments?.length || 0) !== (prev.suggestedActions?.appointments?.length || 0) ||
            (newStructured.suggestedActions?.referrals?.length || 0) !== (prev.suggestedActions?.referrals?.length || 0) ||
            (newStructured.suggestedActions?.prescriptions?.length || 0) !== (prev.suggestedActions?.prescriptions?.length || 0) ||
            (newStructured.suggestedActions?.medicalHistoryItems?.length || 0) !== (prev.suggestedActions?.medicalHistoryItems?.length || 0) ||
            (newStructured.recommendations?.length || 0) !== (prev.recommendations?.length || 0);
          if (changed) {
            lastStructuredRef.current = newStructured;
          }
        }
      }
      const partialStructured = lastStructuredRef.current;

      // Phase indicator — derived from which section markers have streamed so
      // far. The four SOAP text sections stream visibly into their boxes, but
      // everything after "plan" (vitals, medications, suggestedActions,
      // medicalHistoryItems, recommendations) has no visible stream target, so
      // without this the UI looks frozen right after the Plan finishes writing.
      let phase: string;
      if (buffer.includes('"recommendations"')) {
        phase = 'Finalizing note…';
      } else if (buffer.includes('"medicalHistoryItems"')) {
        phase = 'Extracting medical history…';
      } else if (
        buffer.includes('"suggestedActions"') ||
        buffer.includes('"labOrders"')
      ) {
        phase = 'Extracting actions & orders…';
      } else if (buffer.includes('"medications"')) {
        phase = 'Extracting medications…';
      } else if (buffer.includes('"vitals"')) {
        phase = 'Extracting vital signs…';
      } else if (partialSoap.plan) {
        phase = 'Writing plan…';
      } else if (partialSoap.assessment) {
        phase = 'Writing assessment…';
      } else if (partialSoap.objective) {
        phase = 'Writing objective…';
      } else if (partialSoap.subjective) {
        phase = 'Writing subjective…';
      } else {
        phase = 'Generating SOAP note…';
      }
      setStatusMessage(phase);

      // Only emit if we have at least one non-empty SOAP field OR any
      // structured content has appeared.
      const hasStructured =
        (partialStructured?.medications?.length || 0) > 0 ||
        Object.values(partialStructured?.vitals || {}).some((v) => v) ||
        (partialStructured?.suggestedActions?.labOrders?.length || 0) > 0 ||
        (partialStructured?.suggestedActions?.appointments?.length || 0) > 0 ||
        (partialStructured?.suggestedActions?.referrals?.length || 0) > 0 ||
        (partialStructured?.suggestedActions?.prescriptions?.length || 0) > 0 ||
        (partialStructured?.suggestedActions?.medicalHistoryItems?.length || 0) > 0 ||
        (partialStructured?.recommendations?.length || 0) > 0;

      if (
        partialSoap.subjective ||
        partialSoap.objective ||
        partialSoap.assessment ||
        partialSoap.plan ||
        hasStructured
      ) {
        const partialResponse: VoiceToSoapResponse & { __mode: 'partial' } = {
          transcription: '',
          voiceTranscript: '',
          soapNote: partialSoap,
          medicalEntities: { symptoms: [], diagnoses: [], medications: [], procedures: [] },
          vitals: partialStructured?.vitals || {},
          medications: partialStructured?.medications || [],
          suggestedActions: partialStructured?.suggestedActions || {
            labOrders: [],
            imagingOrders: [],
            appointments: [],
            referrals: [],
            prescriptions: [],
          },
          recommendations: partialStructured?.recommendations || [],
          confidence: 0,
          warnings: [],
          processing_time_ms: 0,
          __mode: 'partial',
        } as VoiceToSoapResponse & { __mode: 'partial' };
        onSoapGeneratedRef.current?.(partialResponse);
      }
    });

    const unsubValidationStage = aiGatewayService.on('scribe.validation-stage', (data: ValidationStageEvent) => {
      onValidationStageRef.current?.(data);
    });

    const unsubFollowUp = aiGatewayService.on('scribe.follow-up-response', (data: { updatedSoap?: { soapNote?: VoiceToSoapResponse['soapNote']; vitals?: VoiceToSoapResponse['vitals']; medications?: VoiceToSoapResponse['medications']; suggestedActions?: VoiceToSoapResponse['suggestedActions']; recommendations?: VoiceToSoapResponse['recommendations'] } }) => {
      console.log('Follow-up response:', data);
      setIsProcessing(false);

      if (data.updatedSoap) {
        const updatedResponse: VoiceToSoapResponse & { __mode: 'refine' } = {
          ...currentSoapDataRef.current!,
          soapNote: data.updatedSoap.soapNote || currentSoapDataRef.current!.soapNote,
          vitals: data.updatedSoap.vitals || currentSoapDataRef.current!.vitals,
          medications: data.updatedSoap.medications || currentSoapDataRef.current!.medications,
          suggestedActions: data.updatedSoap.suggestedActions || currentSoapDataRef.current!.suggestedActions,
          recommendations: data.updatedSoap.recommendations || currentSoapDataRef.current!.recommendations || [],
          __mode: 'refine',
        };

        currentSoapDataRef.current = updatedResponse;
        onSoapGeneratedRef.current?.(updatedResponse);
      }
    });

    const unsubTranscriptionPartial = aiGatewayService.on('scribe.transcription-partial', (data: { text: string }) => {
      console.log('Transcription partial:', data);
      setTranscription(prev => prev ? prev + ' ' + data.text : data.text);
    });

    const unsubChatTranscription = aiGatewayService.on('scribe.chat-transcription', (data: { text: string }) => {
      console.log('Transcription-only result:', data);
      setSuccess(true);
      setIsProcessing(false);
      setStatusMessage('Transcription complete!');

      const fullText = data.text;
      onTranscriptionCompleteRef.current?.(fullText);

      const response: VoiceToSoapResponse = {
        transcription: fullText,
        voiceTranscript: fullText,
        soapNote: { subjective: '', objective: '', assessment: '', plan: '' },
        medicalEntities: { symptoms: [], diagnoses: [], medications: [], procedures: [] },
        vitals: {},
        medications: [],
        suggestedActions: { labOrders: [], imagingOrders: [], appointments: [], referrals: [], prescriptions: [] },
        confidence: 0.95,
        warnings: [],
        processing_time_ms: 0,
      } as VoiceToSoapResponse;
      currentSoapDataRef.current = response;
      onSoapGeneratedRef.current?.(response);

      setTimeout(() => {
        setSuccess(false);
        setTranscription('');
      }, 2000);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const unsubSoapResult = aiGatewayService.on('scribe.soap-result', (data: any) => {
      console.log('SOAP result received:', data);
      setSuccess(true);
      setIsProcessing(false);
      setStatusMessage('SOAP note generated successfully!');

      const response: VoiceToSoapResponse = {
        transcription: data.transcription,
        voiceTranscript: data.voiceTranscript || data.transcription,
        soapNote: data.soapNote,
        medicalEntities: {
          symptoms: [],
          diagnoses: [],
          medications: data.medications || [],
          procedures: [],
        },
        vitals: data.vitals || {},
        medications: data.medications || [],
        suggestedActions: data.suggestedActions || {
          labOrders: [],
          imagingOrders: [],
          appointments: [],
          referrals: [],
          prescriptions: [],
        },
        recommendations: data.recommendations || [],
        confidence: data.confidence || 0.95,
        warnings: [],
        processing_time_ms: 0,
      } as VoiceToSoapResponse;

      currentSoapDataRef.current = response;
      onSoapGeneratedRef.current?.(response);
      streamingBufferRef.current = '';
      lastStructuredRef.current = null;

      setTimeout(() => {
        setSuccess(false);
        setTranscription('');
      }, 2000);
    });

    const unsubError = aiGatewayService.on('scribe.error', (data: { message?: string; error?: string }) => {
      console.error('WebSocket error:', data);
      const errorMessage = data.message || data.error || 'WebSocket error occurred';
      setError(errorMessage);
      setIsProcessing(false);
      onErrorRef.current?.(errorMessage);
    });

    // Tab close: free backend scribe state before the socket dies.
    const handleBeforeUnload = () => {
      if (
        !sessionEndedRef.current &&
        sessionIdRef.current &&
        aiGatewayService.isConnected()
      ) {
        sessionEndedRef.current = true;
        try {
          aiGatewayService.scribeEndRecording({
            type: 'end-recording',
            sessionId: sessionIdRef.current,
            patientId: patientIdRef.current,
          });
        } catch {
          /* socket already going down — backend disconnect cleanup will catch it */
        }
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      unmountedRef.current = true;
      if (timerRef.current) clearInterval(timerRef.current);
      if (sendIntervalRef.current) clearInterval(sendIntervalRef.current);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      // Stop the recorder explicitly. Without this, MediaRecorder keeps
      // running off the soon-to-be-stopped MediaStream and its onstop
      // (gated above by unmountedRef) wakes up after we're gone.
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== 'inactive'
      ) {
        try { mediaRecorderRef.current.stop(); } catch { /* already stopped */ }
      }
      if (audioContextRef.current) audioContextRef.current.close();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      // Free backend buffers when the user navigates away mid-record.
      // Same reasoning as useLiveRecording.teardown — the singleton
      // socket stays open across routes, so backend sessionConfigs
      // would otherwise pin RAM until tab close.
      if (
        !sessionEndedRef.current &&
        sessionIdRef.current &&
        aiGatewayService.isConnected()
      ) {
        sessionEndedRef.current = true;
        try {
          aiGatewayService.scribeEndRecording({
            type: 'end-recording',
            sessionId: sessionIdRef.current,
            patientId: patientIdRef.current,
          });
        } catch { /* socket mid-disconnect; backend GC will catch it */ }
      }
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Unsubscribe from all events (singleton manages its own lifecycle)
      unsubConnection();
      unsubConnectionReady();
      unsubSessionStarted();
      unsubChunkReceived();
      unsubProcessing();
      unsubProcessingUpdate();
      unsubValidationStage();
      unsubFollowUp();
      unsubTranscriptionPartial();
      unsubChatTranscription();
      unsubSoapResult();
      unsubError();
    };

  }, []);

  const startRecording = async () => {
    try {
      setError(null);
      setSuccess(false);
      setTranscription('');
      setSilenceWarning(false);
      silenceFramesRef.current = 0;
      setIsInitializing(true);

      sessionIdRef.current = `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      sessionEndedRef.current = false;

      aiGatewayService.scribeStartSession({
        sessionId: sessionIdRef.current,
        patientId,
        medicalRecordId,
        language: 'auto',
        soapSection,
        transcriptionOnly,
      });

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      audioContextRef.current = new AudioContext();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      source.connect(analyserRef.current);

      monitorAudioLevel();

      streamRef.current = stream;
      audioChunksRef.current = [];
      chunkIndexRef.current = 0;

      // Helper: create a recorder that sends its data as a valid WebM when stopped
      const createRecorder = (mediaStream: MediaStream, isFinal = false) => {
        const chunks: Blob[] = [];
        const recorder = new MediaRecorder(mediaStream, {
          mimeType: 'audio/webm;codecs=opus',
        });

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) chunks.push(event.data);
        };

        recorder.onstop = async () => {
          // The 30s rotating-recorder pattern means an `onstop` can fire
          // milliseconds after the component has unmounted (React tore
          // down, but MediaRecorder kept its callback queued). Drop the
          // chunk in that case so we don't ship audio against a session
          // the backend is about to GC.
          if (unmountedRef.current || sessionEndedRef.current) return;

          if (chunks.length > 0 && aiGatewayService.isConnected()) {
            const blob = new Blob(chunks, { type: 'audio/webm' });
            const arrayBuffer = await blob.arrayBuffer();
            aiGatewayService.scribeSendAudioChunk({
              type: 'audio-chunk',
              audioData: arrayBuffer,
              sessionId: sessionIdRef.current,
              chunkIndex: chunkIndexRef.current++,
            });
          }

          if (isFinal) {
            mediaStream.getTracks().forEach(track => track.stop());
            endRecording();
          }
        };

        recorder.start(1000); // 1s timeslice for data availability
        return recorder;
      };

      // Start the first recorder
      const firstRecorder = createRecorder(stream);
      mediaRecorderRef.current = firstRecorder;

      // Mic is live and recorder is capturing — NOW show recording UI
      setIsInitializing(false);
      setIsRecording(true);

      // Every 30s: start a new recorder (1s overlap), then stop the old one
      sendIntervalRef.current = setInterval(() => {
        if (mediaRecorderRef.current?.state === 'recording' && streamRef.current) {
          const oldRecorder = mediaRecorderRef.current;

          // Start new recorder immediately (captures 1s of overlap)
          const newRecorder = createRecorder(streamRef.current);
          mediaRecorderRef.current = newRecorder;

          // Stop old recorder after 1s overlap
          setTimeout(() => {
            if (oldRecorder.state === 'recording') {
              oldRecorder.stop(); // triggers onstop → sends valid WebM chunk
            }
          }, 1000);
        }
      }, 30000);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (err) {
      setIsInitializing(false);
      const errorMessage = err instanceof Error ? err.message : 'Failed to access microphone';
      setError(errorMessage);
      onError?.(errorMessage);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      // Stop the 30s cycling interval
      if (sendIntervalRef.current) {
        clearInterval(sendIntervalRef.current);
        sendIntervalRef.current = null;
      }

      setIsRecording(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }

      // Stop the current recorder — its onstop sends the final chunk
      // Then we stop the stream and call endRecording
      if (mediaRecorderRef.current.state === 'recording') {
        const recorder = mediaRecorderRef.current;
        const existingOnStop = recorder.onstop;

        // Wrap onstop to await the chunk send before finalizing
        recorder.onstop = async function (this: MediaRecorder, e: Event) {
          // Send the last chunk (original async handler)
          if (existingOnStop) {
            await (existingOnStop as ((this: MediaRecorder, ev: Event) => void)).call(this, e);
          }
          // Now the chunk has been emitted — finalize
          streamRef.current?.getTracks().forEach(track => track.stop());
          streamRef.current = null;
          endRecording();
        };

        recorder.stop();
      }

      setRecordingTime(0);
    }
  };

  startRecordingRef.current = startRecording;
  stopRecordingRef.current = stopRecording;

  const endRecording = () => {
    // Mark BEFORE the emit so any concurrent onstop / unmount path
    // doesn't fire a second end-recording or push another chunk.
    sessionEndedRef.current = true;
    aiGatewayService.scribeEndRecording({
      type: 'end-recording',
      sessionId: sessionIdRef.current,
      patientId,
    });

    setStatusMessage('Processing recording...');
    setIsProcessing(true);
  };

  const monitorAudioLevel = () => {
    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

    const updateLevel = () => {
      if (!analyserRef.current) return;

      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      const level = Math.min(100, (average / 255) * 100);
      setAudioLevel(level);

      // Detect sustained silence (~3 seconds at 60fps) and warn user
      if (level < 1) {
        silenceFramesRef.current++;
        if (silenceFramesRef.current > 180) {
          setSilenceWarning(true);
        }
      } else {
        silenceFramesRef.current = 0;
        setSilenceWarning(false);
      }

      animationFrameRef.current = requestAnimationFrame(updateLevel);
    };

    updateLevel();
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`voice-to-soap-recorder-ws ${className}`}>
      {/* Connection Status */}
      <div className="mb-2 flex items-center gap-2 text-xs">
        <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></span>
        <span style={{ color: 'var(--color-text-secondary)' }}>
          {isConnected ? 'Connected to voice service' : 'Connecting...'}
        </span>
      </div>

      <div className="flex items-center gap-4">
        {/* Recording Button */}
        <button
          type="button"
          onClick={isRecording ? stopRecording : startRecording}
          disabled={isProcessing || isInitializing || !isConnected || disabled}
          className={`
            relative flex items-center justify-center w-16 h-16 rounded-full
            transition-all duration-200 shadow-lg
            ${(isProcessing || isInitializing || !isConnected || disabled) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            disabled:opacity-50
          `}
          style={{
            backgroundColor: isRecording ? 'var(--color-error)' : 'var(--color-primary-600)',
          }}
          title={disabled ? 'Please select a patient first' : isInitializing ? 'Starting microphone...' : isRecording ? 'Stop Recording' : 'Start Recording'}
        >
          {isProcessing || isInitializing ? (
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          ) : isRecording ? (
            <StopCircle className="w-8 h-8 text-white" />
          ) : (
            <Mic className="w-8 h-8 text-white" />
          )}
        </button>

        {/* Status Display */}
        <div className="flex-1">
          {isInitializing && (
            <div className="flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Starting microphone...</span>
            </div>
          )}

          {isRecording && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Recording...</span>
                <span className="text-sm font-mono" style={{ color: 'var(--color-text-secondary)' }}>{formatTime(recordingTime)}</span>
              </div>

              {/* Audio Level Visualizer */}
              <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                <div
                  className="h-full transition-all duration-100"
                  style={{ width: `${audioLevel}%`, backgroundColor: 'var(--color-primary-600)' }}
                ></div>
              </div>

              {/* Silence warning */}
              {silenceWarning && (
                <div className="flex items-center gap-2 text-xs px-2 py-1 rounded" style={{ backgroundColor: 'var(--color-warning-bg, #fef3c7)', color: 'var(--color-warning-text, #92400e)' }}>
                  <span>No audio detected — check your microphone is on and selected</span>
                </div>
              )}

              {/* Live transcription during recording */}
              {transcription && (
                <div className="mt-2 p-2 rounded text-sm max-h-32 overflow-y-auto" style={{ backgroundColor: 'var(--color-bg-secondary)', color: 'var(--color-text-primary)' }}>
                  <div className="font-medium mb-1">Transcription:</div>
                  <div className="italic">{transcription}</div>
                </div>
              )}
            </div>
          )}

          {isProcessing && (
            <div className="space-y-2">
              <div className="flex items-center gap-2" style={{ color: 'var(--color-primary-600)' }}>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm font-medium">{statusMessage}</span>
              </div>

              {transcription && (
                <div className="mt-2 p-2 rounded text-sm" style={{ backgroundColor: 'var(--color-bg-secondary)', color: 'var(--color-text-primary)' }}>
                  <div className="font-medium mb-1">Transcription:</div>
                  <div className="italic">{transcription}</div>
                </div>
              )}
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2" style={{ color: 'var(--color-success)' }}>
              <CheckCircle className="w-5 h-5" />
              <span className="text-sm font-medium">{statusMessage}</span>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2" style={{ color: 'var(--color-error)' }}>
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {!isRecording && !isProcessing && !success && !error && (
            <div className="text-sm" style={{ color: disabled ? 'var(--color-text-tertiary)' : 'var(--color-text-secondary)' }}>
              {disabled ? (
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  <span>Please select a patient to enable voice recording</span>
                </div>
              ) : (
                <>
                  Click the microphone to start recording your clinical notes
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
